import { env } from "./env";

export const enterprisePushBlockId = "69b28a5c299a50ff6b187e16";
export const localVoiceId = "qNkzaJoHLLdpvgh5tISm";

export const publicUrls = {
  signIn: `${env.NEXT_PUBLIC_WEB_APP_URL}/sign-in`,
  signUp: `${env.NEXT_PUBLIC_WEB_APP_URL}/sign-up`,
};

/**
 * Wraps an app destination into a sign-up URL that resumes the deep link after
 * auth, e.g. `/app/image-video?model=…` → `…/app/sign-up?redirect=%2Fapp%2Fimage-video%3Fmodel%3D…`.
 */
export function buildSignUpRedirectUrl(destinationUrl: string): string {
  const dest = new URL(destinationUrl, env.NEXT_PUBLIC_WEB_APP_URL);
  const signUp = new URL(publicUrls.signUp);
  signUp.searchParams.set("redirect", `${dest.pathname}${dest.search}`);
  return signUp.toString();
}

// not included in the site map
export const appUrls = {
  root: env.NEXT_PUBLIC_WEB_APP_URL,
  developers: `${env.NEXT_PUBLIC_WEB_APP_URL}/developers`,
  apiKeys: `${env.NEXT_PUBLIC_WEB_APP_URL}/developers/api-keys`,
  soundEffects: `${env.NEXT_PUBLIC_WEB_APP_URL}/sound-effects/generate`,
  collections: `${env.NEXT_PUBLIC_WEB_APP_URL}/voice-library/collections`,
  textToSpeech: `${env.NEXT_PUBLIC_WEB_APP_URL}/speech-synthesis/text-to-speech`,
  voiceChanger: `${env.NEXT_PUBLIC_WEB_APP_URL}/speech-synthesis/speech-to-speech`,
  speechToText: `${env.NEXT_PUBLIC_WEB_APP_URL}/speech-to-text`,
  dubbing: `${env.NEXT_PUBLIC_WEB_APP_URL}/dubbing`,
  convAI: `${env.NEXT_PUBLIC_WEB_APP_URL}/agents`,
  convAIIntegrations: `${env.NEXT_PUBLIC_WEB_APP_URL}/agents/integrations`,
  convAIWebhooks: `${env.NEXT_PUBLIC_WEB_APP_URL}/agents/settings`,
  agentTemplates: `${env.NEXT_PUBLIC_WEB_APP_URL}/agents/templates`,
  music: `${env.NEXT_PUBLIC_WEB_APP_URL}/music`,
  voices: `${env.NEXT_PUBLIC_WEB_APP_URL}/voice-library`,
  imageVideo: `${env.NEXT_PUBLIC_WEB_APP_URL}/image-video`,
  elevenReader: `https://elevenreader.io`,
  voiceIsolator: `${env.NEXT_PUBLIC_WEB_APP_URL}/voice-isolator`,
  voiceLibrary: `${env.NEXT_PUBLIC_WEB_APP_URL}/voice-library`,
  voiceDesign: `${env.NEXT_PUBLIC_WEB_APP_URL}/voice-lab?action=create&creationType=voiceDesign`,
  voiceCloneIVC: `${env.NEXT_PUBLIC_WEB_APP_URL}/voice-lab?action=create&creationType=cloneVoice`,
  voiceClonePVC: `${env.NEXT_PUBLIC_WEB_APP_URL}/voice-lab?action=create&creationType=professionalVoiceClone`,
  copyVoiceFromInternalUser: `${env.NEXT_PUBLIC_WEB_APP_URL}/voice-lab?action=copy`,
  subscribe: `${env.NEXT_PUBLIC_WEB_APP_URL}/subscription`,
  speechEngine: `${env.NEXT_PUBLIC_SERVER_URL}/docs/overview/capabilities/speech-engine`,
  iconicVoices: `${env.NEXT_PUBLIC_WEB_APP_URL}/iconic-voices`,
  // This is creative templates
  templates: `${env.NEXT_PUBLIC_WEB_APP_URL}/templates`,
  supportPage: "https://help.elevenlabs.io",
  supportForm: "https://help.elevenlabs.io/hc/en-us/requests/new?ticket_form_id=13145996177937",
  googleOneTapRedirect: `${env.NEXT_PUBLIC_WEB_APP_URL}/post-marketing-signup`,
};

export const defaultSupportAgentId = "SHdjhie9CClgAqmoUs2q";

/**
 * Maps marketing site pathnames to app paths for redirect purposes.
 * Used by Google One Tap to redirect users to the appropriate app page
 * based on where they signed up from.
 *
 * @param pathname - The current marketing site pathname (e.g., "/text-to-speech")
 * @returns The corresponding app path (e.g., "/speech-synthesis/text-to-speech")
 */
export function getAppRedirectPath(pathname: string): string {
  const mappings: Array<{ pattern: RegExp; path: string }> = [
    { pattern: /^\/text-to-speech(\/|$)/, path: "/speech-synthesis/text-to-speech" },
    { pattern: /^\/voice-changer(\/|$)/, path: "/speech-synthesis/speech-to-speech" },
    { pattern: /^\/speech-to-text(\/|$)/, path: "/speech-to-text" },
    { pattern: /^\/realtime-speech-to-text(\/|$)/, path: "/speech-to-text" },
    { pattern: /^\/voice-design(\/|$)/, path: "/voice-lab?action=create&creationType=voiceDesign" },
    { pattern: /^\/voice-library(\/|$)/, path: "/voice-library" },
    { pattern: /^\/voice-isolator(\/|$)/, path: "/voice-isolator" },
    { pattern: /^\/sound-effects(\/|$)/, path: "/sound-effects/generate" },
    { pattern: /^\/music(\/|$)/, path: "/music" },
    { pattern: /^\/translate-audio(\/|$)/, path: "/dubbing" },
    { pattern: /^\/translate-video(\/|$)/, path: "/dubbing" },
    { pattern: /^\/dubbing-studio(\/|$)/, path: "/dubbing" },
    { pattern: /^\/image-video(\/|$)/, path: "/image-video" },
    { pattern: /^\/studio(\/|$)/, path: "/studio" },
    { pattern: /^\/productions(\/|$)/, path: "/productions" },
    { pattern: /^\/creative-platform(\/|$)/, path: "/home" },
    { pattern: /^\/agents(\/|$)/, path: "/agents" },
    { pattern: /^\/audio-to-text(\/|$)/, path: "/speech-to-text" },
    { pattern: /^\/video-to-text(\/|$)/, path: "/speech-to-text" },
    { pattern: /^\/mp3-to-text(\/|$)/, path: "/speech-to-text" },
    { pattern: /^\/tiktok-transcript-generator(\/|$)/, path: "/dubbing" },
    { pattern: /^\/instagram-transcript-generator(\/|$)/, path: "/dubbing" },
    { pattern: /^\/caption-generator(\/|$)/, path: "/dubbing" },
    { pattern: /^\/ai-voice-generator(\/|$)/, path: "/speech-synthesis/text-to-speech" },
    { pattern: /^\/subtitle-generator(\/|$)/, path: "/dubbing" },
    { pattern: /^\/youtube-transcript-generator(\/|$)/, path: "/dubbing" },
  ];

  for (const { pattern, path } of mappings) {
    if (pattern.test(pathname)) {
      return path;
    }
  }

  // Default to app home
  return "/home";
}
